import 'firebase/auth';
import Vue from 'vue';
import VueCookies from 'vue-cookies';
import {
  checkOrgMigration,
  ensureMigrationChecked,
  updateMigrationCache,
} from '@/utils/checkOrgMigration';
import { isSharedShopPage } from '@/constants/sharedShopPages';
import {
  isV2PlatformSource,
  markFromV2Platform,
  sanitizeRedirectPath,
} from '@/utils/platformMigrationHelper';
Vue.use(VueCookies);

export default async function loginSuccessHandler({
  response,
  payload,
  commit,
  router,
}) {
  if (isV2PlatformSource(payload.source)) {
    markFromV2Platform();
  }

  const loginData = response.data.data;
  const organizations =
    loginData.organizations || loginData.user.organizations;
  const migratedPlatformUrl = loginData.migratedPlatformUrl;
  const accessToken = loginData.token.accessToken;

  // Persist JWT before any post-login navigation or bootstrap HTTP
  localStorage.setItem(
    `${process.env.VUE_APP_ACCESS_TOKEN_NAME}AccessToken`,
    accessToken
  );
  localStorage.setItem(
    `${process.env.VUE_APP_ACCESS_TOKEN_NAME}LoggedIn`,
    'true'
  );
  commit('SET_BEARER', accessToken);

  Vue.$cookies.set(
    `${process.env.VUE_APP_ACCESS_TOKEN_NAME}RefreshToken`,
    loginData.token.refreshToken,
    loginData.token.refreshTokenExpiresIn,
    '',
    window.location.host,
    true
  );

  if (payload.rememberMe === true) {
    Vue.$cookies.set(
      `${process.env.VUE_APP_ACCESS_TOKEN_NAME}HasCredRemembered`,
      true,
      loginData.token.refreshTokenExpiresIn,
      '',
      window.location.host,
      true
    );
  }

  localStorage.setItem(
    `${process.env.VUE_APP_ACCESS_TOKEN_NAME}FirstName`,
    loginData.user.firstName
  );
  localStorage.setItem(
    `${process.env.VUE_APP_ACCESS_TOKEN_NAME}User`,
    JSON.stringify(loginData.user)
  );

  if (payload.orderId) {
    localStorage.setItem('orderId', payload.orderId);
  }

  let organization = loginData.user.organizations[0];

  if (loginData.user.organizations.length > 0) {
    if (payload.iframe_view && payload.organizationId) {
      organization = loginData.user.organizations.find(
        org => org._id === payload.organizationId
      );
      sessionStorage.setItem('doctorClinicSelectedClinic', payload.clinicId);
    }

    localStorage.setItem(
      `${process.env.VUE_APP_ACCESS_TOKEN_NAME}Role`,
      organization.role
    );
    localStorage.setItem(
      `${process.env.VUE_APP_ACCESS_TOKEN_NAME}OrganizationId`,
      organization._id
    );
  }

  // Update user details
  commit('UPDATE_USER_INFO', loginData.user, { root: true });

  const activeOrgId = organization && organization._id ? organization._id : null;
  // One logged decision per login: the API check logs only a redirect, and the
  // check on the login response logs whatever it decides.
  const redirectedFromApi = await ensureMigrationChecked({ trigger: 'login' });
  if (redirectedFromApi) {
    return;
  }

  if (organizations) {
    updateMigrationCache(organizations, migratedPlatformUrl);
  }
  if (
    checkOrgMigration(organizations, migratedPlatformUrl, activeOrgId, null, {
      trigger: 'login',
      skipped: true,
    })
  ) {
    return;
  }

  if (
    response.data.data.user.organizations.length > 0 &&
    organization.role.toUpperCase() !== 'Staff'.toUpperCase()
  ) {
    if (organization.role.toUpperCase() === 'superAdmin'.toUpperCase()) {
      router.push('/super-admin/dashboard');
    }
    if (
      organization.role.toUpperCase() === 'franchise'.toUpperCase() ||
      organization.role === 'subFranchise'.toUpperCase()
    ) {
      router.push('/franchise/notice-board');
    }
    if (
      organization.role.toUpperCase() === 'clinic'.toUpperCase() ||
      organization.role === 'subClinic'.toUpperCase()
    ) {
      router.push('/clinic/notice-board');
    }

    if (organization.role.toUpperCase() === 'doctorClinic'.toUpperCase()) {
      router.push('/doctor-clinic/shop');
    }

    if (organization.role.toUpperCase() === 'org owner'.toUpperCase()) {
      if (payload.iframe_view) {
        if (payload.redirect) {
          return router.push(sanitizeRedirectPath(payload.redirect));
        }
        if (payload.orderId) {
          router.push('/org-owner/checkout');
        } else if (payload.productId) {
          router.push(`/org-owner/product/${payload.productId}`);
        } else if (payload.target === 'compliance') {
          router.push('/org-owner/compliance');
        } else if (payload.target === 'resource-hub') {
          router.push('/org-owner/resources');
        } else {
          router.push('/org-owner/shop');
        }
      } else {
        if (router.currentRoute.query.requestId) {
          router.push(
            '/org-owner/checkout/' + router.currentRoute.query.sharedProductId
          );
        } else if (router.currentRoute.query.sharedProductId) {
          router.push(
            '/org-owner/product/' + router.currentRoute.query.sharedProductId
          );
        } else if (
          router.currentRoute.query.sharedbrandId &&
          router.currentRoute.query.sharedbrandSlug
        ) {
          router.push(
            '/org-owner/shop/brands/' +
              router.currentRoute.query.sharedbrandSlug
          );
        } else if (
          router.currentRoute.query.sharedCategoryId &&
          router.currentRoute.query.sharedCategorySlug
        ) {
          router.push(
            '/org-owner/shop/category/' +
              router.currentRoute.query.sharedCategorySlug
          );
        } else if (isSharedShopPage(router.currentRoute.query.sharedShopPage)) {
          router.push(
            '/org-owner/shop/' + router.currentRoute.query.sharedShopPage
          );
        } else {
          router.push('/org-owner/home');
        }
      }
    }

    if (organization.role.toUpperCase() === 'dermaSpecialist'.toUpperCase()) {
      router.push('/derma-specialist/shop');
    }

    if (organization.role.toUpperCase() === 'doctor'.toUpperCase()) {
      router.push('/doctor/notice-board');
    }

    if (organization.role.toUpperCase() === 'supplier'.toUpperCase()) {
      router.push('/supplier/insights');
    }

    if (organization.role.toUpperCase() === 'nurse'.toUpperCase()) {
      if (payload.iframe_view) {
        if (payload.orderId) {
          router.push('/nurse/checkout');
        } else if (payload.productId) {
          router.push(`/nurse/product/${payload.productId}`);
        } else {
          router.push('/nurse/shop');
        }
      } else {
        router.push('/nurse/home');
      }
    }

    if (organization.role.toUpperCase() === 'admin'.toUpperCase()) {
      router.push('/admin/dashboard');
    }
  } else {
    if (response.data.data.user.userType === 'superAdmin') {
      if (router.currentRoute.query.requestId) {
        router.push(
          '/super-admin/checkout/' + router.currentRoute.query.requestId
        );
      } else if (router.currentRoute.query.sharedProductId) {
        router.push(
          '/super-admin/store/product/' +
            router.currentRoute.query.sharedProductId
        );
      } else if (
        router.currentRoute.query.sharedbrandId &&
        router.currentRoute.query.sharedbrandSlug
      ) {
        router.push(
          '/super-admin/store/shop/brands/' +
            router.currentRoute.query.sharedbrandSlug
        );
      } else if (
        router.currentRoute.query.sharedCategoryId &&
        router.currentRoute.query.sharedCategorySlug
      ) {
        router.push(
          '/super-admin/store/shop/category/' +
            router.currentRoute.query.sharedCategorySlug
        );
      } else if (isSharedShopPage(router.currentRoute.query.sharedShopPage)) {
        router.push(
          '/super-admin/store/shop/' + router.currentRoute.query.sharedShopPage
        );
      } else {
        router.push('/super-admin/dashboard');
      }
    }

    if (
      (response.data.data.user.userType === 'franchise' &&
        response.data.data.user.isProfileCompleted) ||
      response.data.data.user.userType === 'subFranchise'
    ) {
      router.push('/franchise/dashboard');
    }

    if (
      (response.data.data.user.userType === 'clinic' &&
        response.data.data.user.isProfileCompleted) ||
      response.data.data.user.userType === 'subClinic'
    ) {
      if (
        response.data.data.user.userType === 'clinic' &&
        (!response.data.data.user.financialDetails ||
          !response.data.data.user.financialDetails.accountNumber)
      ) {
        router.push('/clinic/setup-bank-detail');
      } else {
        router.push('/clinic/dashboard');
      }
    }

    if (response.data.data.user.userType === 'clinic') {
      router.push('/clinic/notice-board');
    }

    if (response.data.data.user.userType === 'subClinic')
      router.push('/clinic/notice-board');

    if (
      response.data.data.user.userType === 'franchise' ||
      response.data.data.user.userType === 'subFranchise'
    ) {
      router.push('/franchise/notice-board');
    }

    const userType = response.data.data.user.userType;

    if (userType === 'doctor' && process.env.VUE_APP_REGION === 'US') {
      setTimeout(() => {
        window.location = process.env.VUE_APP_DOCTOR_PORTAL;
      }, 0);
      return;
    }

    handleUserRedirection(userType, payload, router);
  }
}

const handleUserRedirection =(userType, payload, router)  => {
  const userConfig = {
    doctor: { defaultRoute: '/doctor/notice-board' },
    nurse: { defaultRoute: '/nurse/home' },
    basic: { defaultRoute: '/basic/notice-board' },
    admin: { defaultRoute: '/admin/dashboard', skipIframeLogic: true },
    dermaSpecialist: { defaultRoute: '/derma-specialist/shop', skipIframeLogic: true },
    doctorClinic: { defaultRoute: '/doctor-clinic/shop', skipIframeLogic: true },
  };

  const config = userConfig[userType] || { defaultRoute: `/${userType}/shop` };
  const basePath = `/${userType}`;

  // For user types that only need simple redirect (admin, dermaSpecialist, doctorClinic)
  if (config.skipIframeLogic) {
    return router.push(config.defaultRoute);
  }

  // Handle iframe_view redirection
  if (payload.iframe_view) {
    if (payload.orderId) {
      return router.push(`${basePath}/checkout`);
    }
    if (payload.productId) {
      return router.push(`${basePath}/product/${payload.productId}`);
    }
    if (payload.target === 'resource-hub') {
      return router.push(`${basePath}/resources`);
    }
    // Default for iframe_view
    return router.push(`${basePath}/shop`);
  }

  // Handle non-iframe_view redirection with query parameters
  const query = router.currentRoute.query;
  
  if (query.requestId) {
    return router.push(`${basePath}/checkout/${query.requestId}`);
  }
  
  if (query.sharedProductId) {
    return router.push(`${basePath}/product/${query.sharedProductId}`);
  }
  
  if (query.sharedbrandId && query.sharedbrandSlug) {
    return router.push(`${basePath}/shop/brands/${query.sharedbrandSlug}`);
  }
  
  if (query.sharedCategoryId && query.sharedCategorySlug) {
    return router.push(`${basePath}/shop/category/${query.sharedCategorySlug}`);
  }

  if (query.sharedShopPage && isSharedShopPage(query.sharedShopPage)) {
    return router.push(`${basePath}/shop/${query.sharedShopPage}`);
  }
  
  // Default route
  return router.push(config.defaultRoute);
};